// Context-aware booking + phone link generators for Pilot's plan picks.
// The catalog doesn't yet have website_url/phone columns populated, so
// we generate links on the fly that always work:
//
// - Hotels: program-deep-link (hyatt.com etc) + Google Maps + Google
//   phone search + booking aggregator search
// - Restaurants: OpenTable + Maps + phone search + menu search
// - Activities: GetYourGuide + Viator + Maps + official tickets search
// - Flights: airline-program booking URL (Aeroplan, Flying Blue, etc.) +
//   seats.aero verify link + award-chart lookup + transfer-source hint
//
// City context is appended so "Pestana Palace Lisbon" finds the right
// property instead of any Pestana hotel.

function gsearch(query) {
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
}
function gmaps(query) {
  return `https://www.google.com/maps/search/${encodeURIComponent(query)}`;
}
function gimages(query) {
  return `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(query)}`;
}
// Quick photo lookup — opens Google Images for the item. Until we
// have a real CDN image database, this is the fastest way to give
// users visual confirmation of what they're picking.
export function photoLookup(query) {
  return gimages(query);
}

const HOTEL_PROGRAM_BASE = {
  'World of Hyatt': 'https://www.hyatt.com/search/',
  'Marriott Bonvoy': 'https://www.marriott.com/search/findHotels.mi?destination=',
  'Hilton Honors': 'https://www.hilton.com/en/search/?query=',
  'IHG One Rewards': 'https://www.ihg.com/hotels/us/en/find-hotels/hotel/search?qDest=',
  'Accor Live Limitless': 'https://all.accor.com/ssr/app/accor/search?destination=',
};

export function getHotelLinks(hotel, city) {
  if (!hotel) return [];
  const name = hotel.hotel_name || '';
  const program = hotel.hotel_program;
  const cityScoped = city ? `${name} ${city}` : name;
  const programLink = program && HOTEL_PROGRAM_BASE[program]
    ? `${HOTEL_PROGRAM_BASE[program]}${encodeURIComponent(cityScoped)}`
    : null;
  return [
    programLink && { label: `Book on ${program.split(' ')[0]}.com`, url: programLink, primary: true },
    { label: '📸 Photos', url: gimages(cityScoped) },
    { label: 'Maps + reviews', url: gmaps(cityScoped) },
    { label: 'Phone', url: gsearch(`${cityScoped} phone number`) },
    !programLink && { label: 'Search to book', url: gsearch(`${cityScoped} reservations`) },
  ].filter(Boolean);
}

export function getRestaurantLinks(restaurant, city) {
  if (!restaurant) return [];
  const name = restaurant.name || '';
  const cityScoped = city ? `${name} ${city}` : name;
  const cuisine = restaurant.cuisine || '';
  return [
    { label: 'OpenTable / reserve', url: `https://www.opentable.com/s?term=${encodeURIComponent(name)}`, primary: true },
    { label: '📸 Photos', url: gimages(`${cityScoped} restaurant food`) },
    { label: 'Maps + reviews', url: gmaps(cityScoped) },
    { label: 'Call', url: gsearch(`${cityScoped} ${cuisine} restaurant phone`) },
    { label: 'Menu', url: gsearch(`${cityScoped} menu`) },
  ];
}

export function getActivityLinks(activity, city) {
  if (!activity) return [];
  const name = activity.name || '';
  const cityScoped = city ? `${name} ${city}` : name;
  return [
    { label: 'GetYourGuide', url: `https://www.getyourguide.com/s/?q=${encodeURIComponent(cityScoped)}`, primary: true },
    { label: '📸 Photos', url: gimages(cityScoped) },
    { label: 'Viator', url: `https://www.viator.com/searchResults/all?text=${encodeURIComponent(cityScoped)}` },
    { label: 'Maps + tickets', url: gmaps(cityScoped) },
    { label: 'Official site', url: gsearch(`${cityScoped} official tickets`) },
  ];
}

const FLIGHT_BOOKING_URL = {
  aeroplan: 'https://www.aircanada.com/aeroplan/redeem',
  united: 'https://www.united.com/en/us/fsr/choose-flights',
  flyingblue: 'https://www.airfrance.us/flying-blue',
  lifemiles: 'https://www.lifemiles.com/',
  virginatlantic: 'https://www.virginatlantic.com/flying-club',
  american: 'https://www.aa.com/aadvantage',
  iberia: 'https://www.iberia.com/avios',
  ba: 'https://www.britishairways.com/executive-club',
};

const FLIGHT_TRANSFER_HINT = {
  aeroplan: 'Transfer from Amex MR or Chase UR (1:1, instant)',
  united: 'Transfer from Chase UR (1:1, instant)',
  flyingblue: 'Transfer from Amex MR or Chase UR (1:1, instant)',
  lifemiles: 'Transfer from Amex MR or Citi TY (1:1, instant — watch for 2x bonuses)',
  virginatlantic: 'Transfer from Amex MR or Chase UR or Citi TY (1:1, instant)',
  american: 'Bilt only (1:1, instant)',
  iberia: 'Transfer from Amex MR or Chase UR (1:1, instant)',
  ba: 'Transfer from Amex MR or Chase UR (1:1, instant)',
};

// Step-by-step booking guide for a picked award flight. Non-experts don't
// know HOW to actually transfer + book — this walks them through it
// one tap at a time. Generates from program + route + dates so it works
// for any picked award without needing per-route DB enrichment.
// Detailed step-by-step transfer instructions per source currency.
// Walks user through the exact UI flow on each issuer's site.
const TRANSFER_INSTRUCTIONS = {
  amex_mr: {
    name: 'Amex Membership Rewards',
    login: 'https://www.americanexpress.com/rewards/',
    steps: 'Log into Amex.com → Rewards & Benefits → "Points" → Use Points → "Transfer to Airlines & Hotels" → search the program name → enter the exact miles amount → confirm. Need a linked frequent flyer number with the airline.',
  },
  chase_ur: {
    name: 'Chase Ultimate Rewards',
    login: 'https://ultimaterewards.chase.com/',
    steps: 'Log into Chase.com → click your Sapphire / Ink / Freedom card → "Earn / Use" → "Combine Points" first if your points are on multiple cards → then "Transfer Points" → pick airline → enter amount in increments of 1000. Need a linked FF number.',
  },
  capital_one: {
    name: 'Capital One Venture Miles',
    login: 'https://venture.capitalone.com/',
    steps: 'Log into Capital One → Rewards → "Transfer Miles" → choose airline partner → enter miles. Most partners 1:1, some 2:1 (older agreements). Allow 24-48h for transfer.',
  },
  citi_ty: {
    name: 'Citi ThankYou Points',
    login: 'https://www.thankyou.com/',
    steps: 'Log into Citi.com → Rewards → ThankYou portal → Transfer Points → pick airline. Some transfers (KrisFlyer, Turkish) have 2:1 ratio bonuses periodically.',
  },
  bilt: {
    name: 'Bilt Rewards',
    login: 'https://www.biltrewards.com/',
    steps: 'Open Bilt app → Travel → Transfer → pick program → enter amount. Bilt is the ONLY way to transfer to Alaska Mileage Plan + American AAdvantage from a transferable currency. Watch for Bilt "Rent Day" (1st of month) for transfer bonuses.',
  },
};

export function getFlightBookingSteps(picked, fromProgram) {
  if (!picked) return [];
  const src = (picked.source || '').toLowerCase();
  const milesNeeded = picked.miles_per_person * 2; // for 2 travelers
  const programName = picked.source;
  const bookingUrl = FLIGHT_BOOKING_URL[src];
  const transferHint = FLIGHT_TRANSFER_HINT[src];
  const isInstant = !src.includes('ana');
  const fundFrom = fromProgram || (picked.funded_by && picked.funded_by[0]);
  const transferDetail = fundFrom && TRANSFER_INSTRUCTIONS[fundFrom];

  return [
    {
      step: 1,
      title: `Transfer ${milesNeeded.toLocaleString()} miles to ${programName}${transferDetail ? ` from ${transferDetail.name}` : ''}`,
      detail: transferDetail
        ? `${transferDetail.steps} ${isInstant ? 'Transfer is INSTANT (usually <5 min).' : 'ANA transfers take 2-3 BUSINESS days — plan accordingly.'} Ratio: 1:1 (transfer 100K = get 100K). ${transferHint || ''}`
        : (transferHint || 'Transfer from one of your transferable currencies. Most are 1:1 instant.'),
      url: transferDetail?.login,
      warning: !isInstant ? '⚠ ANA transfers take 2-3 days — do NOT transfer until you confirm availability + know you can book within the next few days.' : '⚠ Confirm availability on the airline website BEFORE transferring. Transfers are IRREVERSIBLE — once miles leave your bank they can\'t come back.',
    },
    {
      step: 2,
      title: 'Verify the award is still live',
      detail: `Go to ${bookingUrl || 'the airline website'} and search ${picked.route} for ${picked.date}. Confirm there's a business class award at ${picked.miles_per_person.toLocaleString()} miles before you transfer.`,
      url: bookingUrl,
    },
    {
      step: 3,
      title: 'Book the flight',
      detail: `Once miles arrive (${isInstant ? 'usually within minutes' : '2-3 days'}), search again on ${programName} website. Select ${picked.route} for ${picked.date}. ${picked.direct ? 'Pick the DIRECT option.' : 'Choose the routing with shortest layovers.'} Pay $${picked.taxes_total} in taxes + fees.`,
      url: bookingUrl,
    },
    {
      step: 4,
      title: 'Enter passenger info + confirm',
      detail: 'Have passport details ready (full name as on passport, DOB, gender). Select seats if available (some carriers charge extra). Pay taxes with a credit card that has no foreign transaction fees (Amex Plat, Sapphire Reserve, Capital One Venture X all good).',
    },
    {
      step: 5,
      title: 'Save confirmation + airline PNR',
      detail: `Screenshot the confirmation. The PNR (record locator, 6 letters) is what you'll need at the airport. Add it to your wallet app + email it to yourself. ${picked.available_seats <= 2 ? `⚠ Only ${picked.available_seats} seats left at this price — BOOK FAST.` : ''}`,
    },
  ];
}

export function getHotelBookingSteps(hotel, fromProgram) {
  if (!hotel) return [];
  const isPoints = !!hotel.total_points;
  const milesNeeded = hotel.total_points || 0;
  const programName = hotel.hotel_program;
  const bookingUrl = HOTEL_PROGRAM_BASE[programName];
  const isHyatt = programName === 'World of Hyatt';
  const fundFrom = fromProgram || (isHyatt ? 'chase_ur' : programName === 'Hilton Honors' ? 'amex_mr' : null);

  if (!isPoints) {
    return [
      { step: 1, title: 'Compare cash booking sources', detail: 'Check the hotel direct site + Booking.com + Amex Travel (if Amex Plat — for FHR perks). Direct usually wins for loyalty point earning, Amex FHR wins for credit + breakfast.' },
      { step: 2, title: 'Book through Amex FHR if eligible', detail: 'Amex Platinum cardholders book at the same nightly rate but get: $100 hotel credit + 4 PM late checkout + room upgrade (subject to availability) + breakfast for 2. Worth it for stays of 2+ nights.' },
      { step: 3, title: 'Pay with no-foreign-fee card', detail: 'Amex Plat / Chase Sapphire Reserve / Capital One Venture X all have 0% FTF. Booking direct = more loyalty points.' },
    ];
  }

  const transferDetail = fundFrom && TRANSFER_INSTRUCTIONS[fundFrom];

  return [
    {
      step: 1,
      title: `Transfer ${milesNeeded.toLocaleString()} points to ${programName}${transferDetail ? ` from ${transferDetail.name}` : ''}`,
      detail: transferDetail
        ? `${transferDetail.steps} Transfer ${milesNeeded.toLocaleString()} pts at 1:1 ratio (instant). ${isHyatt ? '★ Chase UR → Hyatt is the BEST transfer in the entire points ecosystem — typical 2+ cpp value, much better than burning UR on Pay Yourself Back.' : ''}`
        : `Transfer from your transferable currency. ${isHyatt ? 'Chase UR is the ONLY way to get Hyatt points 1:1 — no other bank transfers to Hyatt at this ratio.' : ''}`,
      url: transferDetail?.login,
      warning: '⚠ Verify award availability before transferring. Award rooms can sell out within hours.',
    },
    {
      step: 2,
      title: 'Search availability + lock the room',
      detail: `Go to ${bookingUrl ? bookingUrl.replace(/\?.*$/, '') : 'the hotel program site'}. Search ${hotel.hotel_name} for your dates. Filter to "Use Points" / "Award nights". Pick the room type you want. ${hotel.amenities && hotel.amenities.length > 0 ? `This property has: ${hotel.amenities.slice(0, 3).join(', ')}.` : ''}`,
      url: bookingUrl,
    },
    {
      step: 3,
      title: 'Apply any elite benefits',
      detail: isHyatt
        ? 'If you have Hyatt Globalist status, you get: 4 PM late checkout (request at booking), free breakfast for 2, suite upgrade if available. Mention status in special requests.'
        : 'Apply any loyalty status perks. Many programs honor "best available rate" guarantee — check if the cash rate dropped after booking points.',
    },
    {
      step: 4,
      title: `Pay $0 + any incidentals at checkout`,
      detail: `Award stays are usually $0 at booking. Resort fees may still apply at some properties (most luxury hotels waive on award stays). ${hotel.phone ? `Call ${hotel.phone} 1 week before arrival to confirm any special requests (anniversary, dietary, view).` : 'Email the hotel 1 week before arrival to confirm requests.'}`,
    },
    {
      step: 5,
      title: 'Save confirmation + screenshot',
      detail: 'Email yourself the confirmation. Add to your wallet app. Note the confirmation number — bring it to the front desk if there\'s ever a question about the rate.',
    },
  ];
}

export function getFlightLinks(picked) {
  if (!picked) return [];
  const src = (picked.source || '').toLowerCase();
  const bookingUrl = FLIGHT_BOOKING_URL[src];
  return [
    bookingUrl && { label: `Book on ${picked.source}`, url: bookingUrl, primary: true },
    { label: 'Verify on seats.aero', url: `https://seats.aero/search?source=${src}` },
    { label: 'Award rules', url: gsearch(`${picked.source} award chart ${picked.route}`) },
    FLIGHT_TRANSFER_HINT[src] && { label: FLIGHT_TRANSFER_HINT[src], info: true },
  ].filter(Boolean);
}

// Renders a row of compact link buttons (used inside the pick cards).
// Caller decides which getXLinks() to call; this just renders.
export function LinkRow({ links }) {
  if (!links || links.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {links.map((l, i) => l.info ? (
        <span
          key={i}
          className="text-[10px] text-brand-ink/65 bg-brand-ink/5 border border-brand-ink/10 rounded px-1.5 py-0.5"
          title={l.label}
        >
          {l.label}
        </span>
      ) : (
        <a
          key={i}
          href={l.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`text-[10px] font-semibold px-2 py-0.5 rounded transition ${
            l.primary
              ? 'bg-teal text-navy hover:bg-teal-dark'
              : 'bg-brand-ink/10 text-brand-ink/85 hover:bg-brand-ink/20'
          }`}
        >
          {l.label} ↗
        </a>
      ))}
    </div>
  );
}
